import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Checkbox,
    Typography,
    FormControlLabel
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearch } from "../../hooks/UseSearch";
import SimpleSearch from "../common/SimpleSearch";
import { useTranslation } from "react-i18next";
import { useActionsData } from "../../store/selectors/actionModulesSelectors";
import { GeneralButton } from "../../common/ui/GeneralButton";
import ActionButton from "../common/table/ActionButton";
import { ActionListItemStyles, StyledDialogListBox } from "./SelectActionModulesDialog.styles";
import { ActionModule } from "../../types/actionModule/actionModule";

interface Props {
    onClose: () => void;
    selectedActions: ActionModule[];
    handleOnSave: (newSelectedActions: ActionModule[]) => void;
}

export default function SelectActionModulesDialog(props: Props) {
    const { searchQuery, handleOnSearch } = useSearch();
    const actionModules = useActionsData();
    const { t } = useTranslation();

    const [selectedActionModules, setSelectedActionModules] = useState(props.selectedActions);

    const filteredActions = useMemo(
        () => searchQuery
            ? actionModules.filter((a) => a.name.toLowerCase().includes(searchQuery.toLowerCase()))
            : actionModules,
        [searchQuery, actionModules]
    );

    useEffect(() => {
        setSelectedActionModules(props.selectedActions)
    }, [props.selectedActions]);

    const isAllSelected = useMemo(() => {
        const selectedActionsSet = new Set(selectedActionModules);
        const selectionEqualToFiltered = selectedActionsSet.size === filteredActions.length &&
            filteredActions.every((e) => selectedActionsSet.has(e));

        return selectionEqualToFiltered;
    }, [filteredActions, selectedActionModules]);

    const onToggleAllSelected = useCallback((isSelected) => {
        if (isSelected) {
            setSelectedActionModules(filteredActions);
        } else {
            setSelectedActionModules([]);
        }
    }, [filteredActions]);

    const actionItems = filteredActions.map((item) => {
        const isSelected = selectedActionModules.find((a) => a.id === item.id) !== undefined;
        return (
            <ActionsListItem
                key={item.id}
                item={item}
                isActionSelected={isSelected}
                handlieIsSelectedChange={(isSelected) => {
                    if (isSelected) {
                        setSelectedActionModules([...selectedActionModules, item]);
                    } else {
                        setSelectedActionModules(selectedActionModules.filter(
                            (action) => action.id !== item.id
                        ));
                    }
                }}
            />
        );
    });

    return (
        <>
            <Dialog
                open={true}
                onClose={props.onClose}
                fullWidth
                maxWidth="sm"
                slotProps={{
                    paper: { sx: { padding: 2, borderRadius: 3 } },
                }}
            >
                <DialogTitle>{t("groupsPage.groups.selectActionModules")}</DialogTitle>
                <DialogContent dividers>
                    <SimpleSearch
                        searchQuery={searchQuery}
                        onSearchHandler={handleOnSearch}
                    />
                    <StyledDialogListBox>
                        {actionItems}
                    </StyledDialogListBox >
                    {
                        filteredActions.length > 0 && <FormControlLabel
                            sx={{ mt: 3 }}
                            control={<Checkbox
                                checked={isAllSelected}
                                onChange={(event) => onToggleAllSelected(event.target.checked)}
                            />}
                            label={t("dialogModals.listSelectAll")} />
                    }
                </DialogContent>
                <DialogActions>
                    <GeneralButton
                        appVariant="secondary"
                        onClick={props.onClose}
                    >
                        {t("buttons.cancel")}
                    </GeneralButton>
                    <GeneralButton onClick={() => props.handleOnSave(selectedActionModules)}>
                        {t("buttons.apply")}
                    </GeneralButton>
                </DialogActions>
            </Dialog>
        </>
    );
};


interface ActionsListItemProps {
    item: ActionModule;
    isActionSelected: boolean;
    handlieIsSelectedChange: (newSelected: boolean) => void;
}

function ActionsListItem({
    item,
    isActionSelected,
    handlieIsSelectedChange,
}: ActionsListItemProps) {
    return (
        <ActionListItemStyles>
            <Box>
                <ActionButton
                    iconName={item.iconName}
                    colorHex={item.colorHex ? item.colorHex : ""}
                    iconUrl={item.iconUrl}
                    actionName={item.name}
                />
                <Typography>{item.name}</Typography>
            </Box>
            <Checkbox
                checked={isActionSelected}
                onChange={(e) => handlieIsSelectedChange(e.target.checked)}
                sx={(theme) => ({
                    color: theme.palette.companyMain.main,
                })}
                slotProps={{ input: { "aria-label": "controlled" } }}
            />
        </ActionListItemStyles>
    );
};